/**
 * Club & Store Analytics Tracking Utility
 * Helper functions to track views and navigation to clubs and stores
 */

import { trackFeatureUsage } from './featureTracking';
import api from '../services/api';

/**
 * Track feature usage (sends to backend analytics endpoint)
 */
const trackFeatureUsageToBackend = async (
  featureName: string,
  action: string,
  screenName?: string,
  metadata?: Record<string, any>,
  userRole?: string
): Promise<void> => {
  try {
    await api.post('/analytics/feature-usage', {
      featureName,
      action,
      platform: 'web',
      screenName,
      metadata,
      userRole,
    });
  } catch (error) {
    // Silently fail - don't break the app if analytics logging fails
    if (import.meta.env.DEV) {
      console.warn('Failed to log feature usage to backend:', error);
    }
  }
};

/**
 * Track when a user views a club (detail page or discovery page)
 * @param clubId - The ID of the club being viewed
 * @param screenName - The screen name (e.g., 'ClubDetails', 'ClubsDiscovery')
 * @param userRole - Optional user role for analytics filtering
 */
export const trackClubView = async (
  clubId: string,
  screenName: string,
  userRole?: string
): Promise<void> => {
  await trackFeatureUsageToBackend(
    'club_view',
    'view',
    screenName,
    { clubId },
    userRole
  );
};

/**
 * Track when a user views a store (detail page or discovery page)
 * @param storeId - The ID of the store being viewed
 * @param screenName - The screen name (e.g., 'StoreDetails', 'StoresDiscovery')
 * @param userRole - Optional user role for analytics filtering
 */
export const trackStoreView = async (
  storeId: string,
  screenName: string,
  userRole?: string
): Promise<void> => {
  await trackFeatureUsageToBackend(
    'store_view',
    'view',
    screenName,
    { storeId },
    userRole
  );
};

/**
 * Track when a user navigates to the clubs discovery section
 * @param userRole - Optional user role for analytics filtering
 */
export const trackClubsDiscoveryView = async (userRole?: string): Promise<void> => {
  await trackFeatureUsageToBackend(
    'clubs_discovery',
    'view',
    'ClubsDiscovery',
    undefined,
    userRole
  );
};

/**
 * Track when a user navigates to the stores discovery section
 * @param userRole - Optional user role for analytics filtering
 */
export const trackStoresDiscoveryView = async (userRole?: string): Promise<void> => {
  await trackFeatureUsageToBackend(
    'stores_discovery',
    'view',
    'StoresDiscovery',
    undefined,
    userRole
  );
};
